<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import Color from '@client/pages/bizcards/studio/designer/editors/color.svelte';

  import { colorPrimary, colorSecondary, colorTertiary } from '@client/stores/brand';

  import { colorToHex } from '@client/utils/color';

  /** @type {string} */
  export let value = '';
  /** @type {(newValue: string) => void} */
  export let onChange = () => {};

  const MIN_COLORS = 1;
  const MAX_COLORS = 5;

  /**
   * @param {string} val
   * @returns {string[] | null}
   */
  const parseColors = (val) => {
    if (!val || val.startsWith('https://')) {
      return null;
    }

    const matches = val.match(/#[0-9A-Fa-f]{6}/g);

    return matches && matches.length > 0 ? matches.slice(0, MAX_COLORS) : null;
  };

  /** @type {string[]} */
  let colors = parseColors(value) || [$colorPrimary || '#474747'];

  onMount(() => {
    const parsed = parseColors(value);
    if (parsed) {
      colors = parsed;
    }
  });

  // Generate gradient and notify parent when colors change
  const updateGradient = () => {
    if (colors.length === 1) {
      onChange(colorToHex(colors[0]));
    } else {
      onChange(colors.map(colorToHex).join(','));
    }
  };

  /**
   * @param {number} index
   * @param {string} newColor
   */
  const updateColor = (index, newColor) => {
    colors[index] = newColor;
    colors = colors;
    updateGradient();
  };

  const addColor = () => {
    const selection = [$colorPrimary, $colorSecondary, $colorTertiary];
    if (colors.length < MAX_COLORS) {
      const newColor = selection.find((c) => c && !colors.includes(c)) || '#FFFFFF';
      colors = [...colors, newColor];
      updateGradient();
    }
  };

  /** @param {number} index */
  const removeColor = (index) => {
    if (colors.length > MIN_COLORS) {
      colors = colors.filter((_, i) => i !== index);
      updateGradient();
    }
  };
</script>

<div class="gradient-editor p-3">
  {#each colors as color, index}
    <div class="color-row">
      <Color value={color} onChange={(newColor) => updateColor(index, newColor)} />
      {#if colors.length > MIN_COLORS}
        <Button size="sm" color="link" class="text-danger" on:click={() => removeColor(index)}>
          <Icon name="trash-2" />
        </Button>
      {/if}
    </div>
  {/each}

  {#if colors.length < MAX_COLORS}
    <Button size="sm" class="add-btn" on:click={addColor}>
      <Icon name="plus" />
      {$format('label.ADD_COLOR')}
    </Button>
  {/if}
</div>

<style lang="scss">
  .gradient-editor {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .color-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
</style>
